// /vouchers/new — Create Voucher (PAGES.md §3.34, FORMS.md §14.1)

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Input,
  TextArea,
  RadioGroup,
  Toggle,
  Select,
  DatePicker,
  Button,
  Alert,
  useToast,
} from '@bbpos/ui';
import { getSubunitMultiplier, majorToAmount } from '@bbpos/utils';
import { ApiError } from '@/lib/api-client';
import { calendarDate } from '@/lib/store-clock';
import {
  useCreateVoucher,
  generateVoucherCode,
  isValidVoucherCode,
  VoucherCodeChip,
  VoucherStatusPill,
  formatVoucherValue,
} from '@/features/vouchers';
import type { VoucherCreateInput, VoucherType } from '@/features/vouchers';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { productService } from '@/services/product.service';
import { categoryService } from '@/services/category.service';
import { locationService } from '@/services/location.service';
import { customersApi } from '@/features/customers';
import {
  mapProductsToScopeOptions,
  mapCategoriesToScopeOptions,
  mapCustomersToScopeOptions,
  type ScopeOption,
} from './voucher-scope-options';

function today(): Date {
  return new Date();
}

function plus30Days(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d;
}

// The picked calendar day, not its UTC date — a local midnight east of UTC
// is the previous UTC day (CU2-006).
function toYMD(d: Date | undefined): string | null {
  if (!d) return null;
  return calendarDate(d);
}

interface FormState {
  auto_generate: boolean;
  code: string;
  type: VoucherType;
  value_percent: string;
  value_rupees: string;
  min_order_rupees: string;
  max_discount_rupees: string;
  max_uses: string;
  is_single_use: boolean;
  starts_at: Date | undefined;
  expires_at: Date | undefined;
  applicable_product_ids: string[];
  applicable_category_ids: string[];
  location_ids: string[];
  customer_ids: string[];
  campaign_name: string;
  description: string;
}

const INITIAL: FormState = {
  auto_generate: true,
  code: generateVoucherCode(8),
  type: 'PERCENTAGE',
  value_percent: '10',
  value_rupees: '',
  min_order_rupees: '',
  max_discount_rupees: '',
  max_uses: '',
  is_single_use: true,
  starts_at: today(),
  expires_at: plus30Days(),
  applicable_product_ids: [],
  applicable_category_ids: [],
  location_ids: [],
  customer_ids: [],
  campaign_name: '',
  description: '',
};

type FieldError = Partial<Record<'code' | 'value' | 'min_order' | 'max_discount' | 'max_uses' | 'expires_at' | 'starts_at', string>>;

function VoucherCreateContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<FieldError>({});
  const create = useCreateVoucher();

  // Scope options (issue #87) — fetch active products and categories so the
  // "Applicable Products" / "Applicable Categories" multi-selects are not empty.
  const [productOptions, setProductOptions] = useState<ScopeOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<ScopeOption[]>([]);
  const [locationOptions, setLocationOptions] = useState<ScopeOption[]>([]);
  const [customerOptions, setCustomerOptions] = useState<ScopeOption[]>([]);
  const [isLoadingScope, setIsLoadingScope] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingScope(true);
    Promise.all([
      productService.list({ limit: 500, status: 'active' }).catch(() => ({ data: [] as { id: string; name: string }[] })),
      categoryService.list({ is_active: true }).catch(() => ({ data: [] as { id: string; name: string }[] })),
      locationService.list({ limit: 100, is_active: 'true' }).catch(() => ({ data: [] as { id: string; name: string }[] })),
      customersApi.list({ limit: 100 }).catch(() => ({ data: [] as { id: string; name: string; phone?: string }[] })),
    ])
      .then(([products, categories, locations, customers]) => {
        if (cancelled) return;
        setProductOptions(mapProductsToScopeOptions(products.data as { id: string; name: string }[]));
        setCategoryOptions(mapCategoriesToScopeOptions(categories.data as { id: string; name: string }[]));
        setLocationOptions(mapProductsToScopeOptions(locations.data as { id: string; name: string }[]));
        setCustomerOptions(mapCustomersToScopeOptions(customers.data as { id: string; name: string; phone?: string }[]));
      })
      .finally(() => { if (!cancelled) setIsLoadingScope(false); });
    return () => { cancelled = true; };
  }, []);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors({});
  }

  function regenerateCode() {
    setForm((f) => ({ ...f, code: generateVoucherCode(8) }));
  }

  function validate(): { ok: boolean; payload?: VoucherCreateInput } {
    const errs: FieldError = {};

    const code = form.code.trim().toUpperCase();
    if (!isValidVoucherCode(code)) {
      errs.code = 'Code must be 3–50 uppercase letters, digits, hyphens, or underscores';
    }

    let value = 0;
    if (form.type === 'PERCENTAGE') {
      const pct = Number(form.value_percent);
      if (!Number.isFinite(pct) || pct < 1 || pct > 100) {
        errs.value = 'Percentage must be between 1 and 100';
      } else {
        value = Math.floor(pct);
      }
    } else {
      const rupees = Number(form.value_rupees);
      if (!Number.isFinite(rupees) || rupees < 1) {
        errs.value = `Flat amount must be at least ${symbol}1`;
      } else {
        value = majorToAmount(rupees, currency);
      }
    }

    let min_order_value: number | undefined;
    if (form.min_order_rupees.trim() !== '') {
      const n = Number(form.min_order_rupees);
      if (!Number.isFinite(n) || n < 0) errs.min_order = 'Min order must be ≥ 0';
      else min_order_value = majorToAmount(n, currency);
    }

    let max_discount: number | null | undefined = undefined;
    if (form.type === 'PERCENTAGE' && form.max_discount_rupees.trim() !== '') {
      const n = Number(form.max_discount_rupees);
      if (!Number.isFinite(n) || n < 0) errs.max_discount = 'Max discount must be ≥ 0';
      else max_discount = majorToAmount(n, currency);
    }

    let max_uses: number | null | undefined = undefined;
    if (form.max_uses.trim() !== '') {
      const n = Number(form.max_uses);
      if (!Number.isFinite(n) || n < 1 || !Number.isInteger(n)) errs.max_uses = 'Max uses must be a positive integer';
      else max_uses = n;
    } else {
      max_uses = null;
    }

    if (!form.starts_at) {
      errs.starts_at = 'Start date is required';
    }

    if (!form.expires_at) {
      errs.expires_at = 'End date is required';
    } else if (form.starts_at && calendarDate(form.starts_at) > calendarDate(form.expires_at)) {
      errs.expires_at = 'End date must be on or after start date';
    }

    setErrors(errs);
    if (Object.keys(errs).length > 0) return { ok: false };

    return {
      ok: true,
      payload: {
        code,
        type: form.type,
        value,
        min_order_value: min_order_value ?? 0,
        max_discount: form.type === 'PERCENTAGE' ? max_discount ?? null : undefined,
        max_uses: max_uses === undefined ? null : max_uses,
        is_single_use: form.is_single_use,
        starts_at: toYMD(form.starts_at),
        expires_at: toYMD(form.expires_at),
        applicable_product_ids: form.applicable_product_ids.length > 0 ? form.applicable_product_ids : undefined,
        applicable_category_ids: form.applicable_category_ids.length > 0 ? form.applicable_category_ids : undefined,
        location_ids: form.location_ids.length > 0 ? form.location_ids : undefined,
        customer_ids: form.customer_ids.length > 0 ? form.customer_ids : undefined,
        campaign_name: form.campaign_name.trim() || undefined,
        description: form.description.trim() || undefined,
      },
    };
  }

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    const result = validate();
    if (!result.ok || !result.payload) return;
    try {
      const created = await create.mutateAsync(result.payload);
      toast({ variant: 'success', title: 'Voucher created', description: `Code ${created.code} is ready to use.` });
      navigate(`/vouchers/${created.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        if (/code/i.test(err.message) || err.message.includes('CRM_VOUCHER_CODE_DUPLICATE')) {
          setErrors((prev) => ({ ...prev, code: 'Voucher code already exists. Try another or enable auto-generate.' }));
          return;
        }
        toast({ variant: 'error', title: 'Could not create voucher', description: err.message });
        return;
      }
      toast({ variant: 'error', title: 'Could not create voucher', description: 'Please try again.' });
    }
  }

  // Keyboard shortcuts: Ctrl+Enter submit, Esc cancel
  const submitRef = useRef<() => Promise<void>>(() => Promise.resolve());
  submitRef.current = () => handleSubmit();
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        void submitRef.current();
      } else if (e.key === 'Escape') {
        navigate('/vouchers');
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // Live preview
  const previewValue = useMemo(() => {
    if (form.type === 'PERCENTAGE') {
      const pct = Number(form.value_percent);
      return formatVoucherValue({ type: 'PERCENTAGE', value: Number.isFinite(pct) ? pct : 0 }, currency);
    }
    const r = Number(form.value_rupees);
    return formatVoucherValue(
      { type: 'FLAT', value: majorToAmount(Number.isFinite(r) ? r : 0, currency) },
      currency,
    );
  }, [form.type, form.value_percent, form.value_rupees, currency]);

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-success-500 via-teal-500 to-brand-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(16,185,129,0.55)]">
        <div className="pointer-events-none absolute -top-24 -right-10 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
            New Voucher
          </span>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Create Discount Code</h1>
          <p className="mt-1.5 text-sm text-white/90 max-w-xl">
            Configure a percentage or flat discount and choose who can redeem it.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Form — 2 of 3 columns on lg */}
        <form
          onSubmit={handleSubmit}
          className="lg:col-span-2 rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 p-6 sm:p-7 animate-slide-up"
        >
          {create.isError && !errors.code ? (
            <div className="mb-5">
              <Alert variant="danger" title="Could not save" description="Check the fields below and try again." />
            </div>
          ) : null}

          {/* Code & Type */}
          <fieldset className="mb-6">
            <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Code & Type</legend>

            <div className="rounded-xl bg-gradient-to-br from-brand-50 to-indigo-50 dark:from-brand-900/20 dark:to-indigo-900/20 p-4 ring-1 ring-brand-100 dark:ring-brand-800/30 mb-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Auto-generate code</p>
                  <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                    We'll generate a short, easy-to-read code for you.
                  </p>
                </div>
                <Toggle
                  checked={form.auto_generate}
                  onChange={(v) => {
                    update('auto_generate', v);
                    if (v) setForm((f) => ({ ...f, code: generateVoucherCode(8) }));
                  }}
                />
              </div>
              {form.auto_generate ? (
                <div className="mt-3 flex items-center gap-3">
                  <VoucherCodeChip code={form.code} size="md" copyable />
                  <button
                    type="button"
                    onClick={regenerateCode}
                    className="text-xs font-semibold text-brand-600 dark:text-brand-300 hover:text-brand-700 dark:hover:text-brand-200"
                  >
                    ↻ Regenerate
                  </button>
                </div>
              ) : (
                <div className="mt-3">
                  <Input
                    label="Code"
                    value={form.code}
                    onChange={(v) => update('code', v.toUpperCase())}
                    error={errors.code}
                    placeholder="SUMMER25"
                    helperText="Uppercase letters, numbers, hyphens, underscores"
                    aria-describedby="code-hint"
                  />
                </div>
              )}
              {form.auto_generate && errors.code ? (
                <p className="mt-2 text-xs font-semibold text-danger-600 dark:text-danger-300">{errors.code}</p>
              ) : null}
            </div>

            <RadioGroup
              label="Discount Type"
              value={form.type}
              onChange={(v) => update('type', v as VoucherType)}
              variant="horizontal"
              options={[
                { value: 'PERCENTAGE', label: 'Percentage off' },
                { value: 'FLAT', label: `Flat ${symbol} off` },
              ]}
            />

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {form.type === 'PERCENTAGE' ? (
                <Input
                  label="Percentage"
                  type="number"
                  min={1}
                  max={100}
                  step={1}
                  suffix={<span className="text-sm text-neutral-500">%</span>}
                  value={form.value_percent}
                  onChange={(v) => update('value_percent', v)}
                  error={errors.value}
                />
              ) : (
                <Input
                  label="Amount"
                  type="number"
                  min={1}
                  step={1 / getSubunitMultiplier(currency)}
                  prefix={<span className="text-sm text-neutral-500">{symbol}</span>}
                  value={form.value_rupees}
                  onChange={(v) => update('value_rupees', v)}
                  error={errors.value}
                />
              )}
              <Input
                label="Min Order Value"
                type="number"
                min={0}
                step={1 / getSubunitMultiplier(currency)}
                prefix={<span className="text-sm text-neutral-500">{symbol}</span>}
                value={form.min_order_rupees}
                onChange={(v) => update('min_order_rupees', v)}
                error={errors.min_order}
                helperText="Leave blank for no minimum"
              />
              {form.type === 'PERCENTAGE' ? (
                <Input
                  label="Max Discount Cap"
                  type="number"
                  min={0}
                  step={1 / getSubunitMultiplier(currency)}
                  prefix={<span className="text-sm text-neutral-500">{symbol}</span>}
                  value={form.max_discount_rupees}
                  onChange={(v) => update('max_discount_rupees', v)}
                  error={errors.max_discount}
                  helperText={`Max ${symbol} discount applied. Blank = no cap.`}
                />
              ) : null}
            </div>
          </fieldset>

          {/* Usage Limits */}
          <fieldset className="mb-6">
            <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Usage Limits</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Total Usage Limit"
                type="number"
                min={1}
                step={1}
                value={form.max_uses}
                onChange={(v) => update('max_uses', v)}
                error={errors.max_uses}
                helperText="Blank = unlimited"
              />
              <div className="rounded-xl bg-neutral-50 dark:bg-neutral-900/40 p-4 ring-1 ring-neutral-100 dark:ring-neutral-700/40 flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Single use per customer</p>
                  <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                    Each customer can only redeem this voucher once.
                  </p>
                </div>
                <Toggle checked={form.is_single_use} onChange={(v) => update('is_single_use', v)} />
              </div>
            </div>
          </fieldset>

          {/* Validity */}
          <fieldset className="mb-6">
            <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Validity Period</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DatePicker
                label="Valid From"
                variant="single"
                value={form.starts_at}
                onChange={(v) => update('starts_at', v)}
                error={errors.starts_at}
              />
              <DatePicker
                label="Valid Until"
                variant="single"
                value={form.expires_at}
                onChange={(v) => update('expires_at', v)}
                error={errors.expires_at}
              />
            </div>
          </fieldset>

          {/* Scope — applicable products/categories */}
          <fieldset className="mb-6">
            <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Scope (optional)</legend>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
              Leave both empty to apply this voucher to all products and categories.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Applicable Products"
                variant="multi-select"
                placeholder={isLoadingScope ? 'Loading…' : 'All products'}
                options={productOptions}
                value={form.applicable_product_ids}
                onChange={(v) => update('applicable_product_ids', Array.isArray(v) ? (v as string[]) : [])}
              />
              <Select
                label="Applicable Categories"
                variant="multi-select"
                placeholder={isLoadingScope ? 'Loading…' : 'All categories'}
                options={categoryOptions}
                value={form.applicable_category_ids}
                onChange={(v) => update('applicable_category_ids', Array.isArray(v) ? (v as string[]) : [])}
              />
            </div>
          </fieldset>

          {/* Restrictions — who and where (QA 7.5.16) */}
          <fieldset className="mb-6">
            <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Restrictions (optional)</legend>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
              Leave both empty and the voucher is valid at every location, for anyone holding the code.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Valid at Locations"
                variant="multi-select"
                placeholder={isLoadingScope ? 'Loading…' : 'All locations'}
                options={locationOptions}
                value={form.location_ids}
                onChange={(v) => update('location_ids', Array.isArray(v) ? (v as string[]) : [])}
              />
              <Select
                label="Only for Customers"
                variant="multi-select"
                placeholder={isLoadingScope ? 'Loading…' : 'Any customer'}
                options={customerOptions}
                value={form.customer_ids}
                onChange={(v) => update('customer_ids', Array.isArray(v) ? (v as string[]) : [])}
              />
            </div>
          </fieldset>

          {/* Campaign & Description */}
          <fieldset className="mb-6">
            <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Details</legend>
            <div className="grid grid-cols-1 gap-4">
              <Input
                label="Campaign Name"
                value={form.campaign_name}
                onChange={(v) => update('campaign_name', v)}
                placeholder="e.g. Diwali 2026 Launch"
                helperText="Optional — for internal tracking"
              />
              <TextArea
                label="Description / Terms"
                value={form.description}
                onChange={(v) => update('description', v)}
                placeholder={`Internal note — e.g. 10% off on all orders above ${symbol}500`}
                maxLength={500}
                rows={3}
              />
            </div>
          </fieldset>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="ghost" type="button" onClick={() => navigate('/vouchers')}>Cancel</Button>
            <Button variant="primary" type="submit" loading={create.isPending}>Create Voucher</Button>
          </div>
          <p className="mt-3 text-xs text-neutral-400 dark:text-neutral-500 text-right">
            Press <kbd className="rounded bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 font-mono text-[10px]">Ctrl+Enter</kbd> to submit · <kbd className="rounded bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 font-mono text-[10px]">Esc</kbd> to cancel
          </p>
        </form>

        {/* Live preview — 1 of 3 columns on lg */}
        <aside className="lg:col-span-1 animate-slide-up" style={{ animationDelay: '60ms' }}>
          <div className="sticky top-4 rounded-2xl bg-gradient-to-br from-brand-600 via-indigo-600 to-violet-600 text-white shadow-[0_25px_70px_-25px_rgba(99,102,241,0.6)] overflow-hidden">
            <div className="pointer-events-none absolute -top-20 -right-10 h-40 w-40 rounded-full bg-white/20 blur-3xl" />
            <div className="relative p-6">
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/80">Preview</p>
              <div className="mt-3 inline-block">
                <VoucherCodeChip code={form.code || 'CODE'} size="lg" />
              </div>
              <p className="mt-4 text-3xl font-bold tracking-tight tabular-nums">{previewValue}</p>
              <p className="mt-1 text-xs text-white/80">
                {form.min_order_rupees ? `Min order ${symbol}${form.min_order_rupees}` : 'No minimum order'}
              </p>
              {form.type === 'PERCENTAGE' && form.max_discount_rupees ? (
                <p className="mt-0.5 text-xs text-white/80">Max discount {symbol}{form.max_discount_rupees}</p>
              ) : null}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <VoucherStatusPill status="ACTIVE" />
                {form.max_uses ? (
                  <span className="text-xs text-white/75 tabular-nums">Max {form.max_uses} uses</span>
                ) : (
                  <span className="text-xs text-white/75">Unlimited uses</span>
                )}
                {form.is_single_use ? (
                  <span className="text-xs text-white/75">· Single use</span>
                ) : null}
              </div>
              <p className="mt-4 text-xs text-white/70 tabular-nums">
                {form.starts_at ? `From ${toYMD(form.starts_at)}` : 'No start date'}
                {' → '}
                {form.expires_at ? toYMD(form.expires_at) : 'No expiry'}
              </p>
              {form.campaign_name ? <p className="mt-2 text-xs text-white/80 font-semibold">{form.campaign_name}</p> : null}
              {form.description ? <p className="mt-1 text-xs text-white/70 italic">"{form.description}"</p> : null}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export function VoucherCreatePage() {
  return (
    <PageContainer
      title="New Voucher"
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Vouchers', href: '/vouchers' }, { label: 'New Voucher' }]}
    >
      <ErrorBoundary resetKey="vouchers-new">
        <VoucherCreateContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
